import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatUSDT, formatDate } from '../../utils/format';
import {
  useMachineDisplay,
  formatXtRange,
  formatUsdtRangeFromXt,
} from '../../hooks/useMachineDisplay';
import { useXtPrice } from '../../hooks/useXtPrice';
import { TIER_LABELS, MACHINE_IMAGES } from '../../utils/constants';

function ThumbImage({ tierIdx }) {
  const [err, setErr] = useState(false);
  const img = MACHINE_IMAGES[tierIdx];
  if (err || !img) return <div className="fallback-icon">⛏</div>;
  return <img src={img} alt="" onError={() => setErr(true)} />;
}

function MyMachineCard({ pkg, index }) {
  const { config } = useMachineDisplay();
  const { xtPrice } = useXtPrice();
  const tierIdx = Number(pkg.tierIndex);
  const name = TIER_LABELS[tierIdx] || `Tier ${tierIdx + 1}`;
  const daysPct = Number(pkg.miningDaysTotal) > 0
    ? Math.min(100, (Number(pkg.miningDaysClaimed) / Number(pkg.miningDaysTotal)) * 100)
    : 0;

  return (
    <div className={`my-machine-card ${pkg.active ? 'active' : 'inactive'}`}>
      <div className="my-machine-thumb">
        <ThumbImage tierIdx={tierIdx} />
      </div>
      <div className="mmc-body">
        <div className="mmc-top">
          <div>
            <div className="mmc-name">#{index + 1} · {name}</div>
            <div className="mmc-price">
              {formatUSDT(pkg.amountUSDT, 0)} USDT · purchased {formatDate(pkg.purchaseTimestamp)}
            </div>
          </div>
          <span className={`badge ${pkg.active ? 'badge-success' : 'badge-muted'}`}>
            {pkg.active ? 'Active' : 'Ended'}
          </span>
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            <span>Mining days</span>
            <span style={{ fontFamily: 'var(--font-mono)' }}>
              {Number(pkg.miningDaysClaimed)} / {Number(pkg.miningDaysTotal)}
            </span>
          </div>
          <div className="mmc-progress">
            <div className="mmc-progress-fill" style={{ width: `${daysPct}%` }} />
          </div>
        </div>
        <div className="mmc-stats">
          <div className="mmc-stat">
            <div className="l">Daily</div>
            <div className="v">{formatXtRange(tierIdx, config)} XT</div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
              ≈ {formatUsdtRangeFromXt(tierIdx, config, xtPrice)} USDT
            </div>
          </div>
          <div className="mmc-stat">
            <div className="l">Earned</div>
            <div className="v">{formatUSDT(pkg.miningEarnedUSDT, 0)} USDT</div>
          </div>
          <div className="mmc-stat">
            <div className="l">Runtime</div>
            <div className="v">{Number(pkg.miningDaysTotal)}d</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MyMachines({ packages }) {
  const navigate = useNavigate();

  if (!packages || packages.length === 0) {
    return (
      <div className="my-machines-empty">
        <div className="icon">⛏</div>
        <h3 style={{ marginBottom: 8 }}>No machines yet</h3>
        <p style={{ marginBottom: 20 }}>Purchase your first XTerra Forge to start mining.</p>
        <button className="btn btn-primary btn-lg" onClick={() => navigate('/invest')}>
          Browse Machines →
        </button>
      </div>
    );
  }

  const active = packages.filter((p) => p.active);
  const inactive = packages.filter((p) => !p.active);

  return (
    <div>
      <div style={{ display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
        <span className="badge badge-success">{active.length} Active</span>
        {inactive.length > 0 && <span className="badge badge-muted">{inactive.length} Ended</span>}
        <span style={{ color: 'var(--text-muted)', fontSize: 13, marginLeft: 'auto' }}>
          Total machines: {packages.length}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[...active, ...inactive].map((pkg, i) => (
          <MyMachineCard key={i} pkg={pkg} index={i} />
        ))}
      </div>
    </div>
  );
}
